import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  LayoutDashboard,
  Wrench,
  Users,
  Package,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Globe,
  ChevronLeft,
  Bell,
  AlertTriangle,
  Cog,
  MoreHorizontal,
  HardHat,
  Megaphone
} from 'lucide-react';

// Navigation items - paths are relative (will be prefixed with basePath)
const navItems = [
  { path: '', label: 'nav_dashboard', icon: LayoutDashboard, module: 'dashboard' },
  { path: '/jobs', label: 'nav_jobs', icon: Wrench, module: 'jobs' },
  { path: '/invoices', label: 'nav_invoices', icon: FileText, module: 'invoices' },
  { path: '/inventory', label: 'nav_inventory', icon: Package, module: 'inventory' },
  { path: '/services', label: 'nav_services', icon: Cog, module: 'services' },
  { path: '/customers', label: 'nav_customers', icon: Users, module: 'customers' },
  { path: '/employees', label: 'nav_employees', icon: HardHat, module: 'employees' },
  { path: '/marketing', label: 'nav_marketing', icon: Megaphone, module: 'marketing', roles: ['owner', 'manager', 'super_admin'] },
  { path: '/website', label: 'nav_website', icon: Globe, module: 'settings', roles: ['owner', 'manager', 'super_admin'] },
  { path: '/reports', label: 'nav_reports', icon: BarChart3, module: 'reports', roles: ['owner', 'manager', 'super_admin'] },
  { path: '/settings', label: 'nav_settings', icon: Settings, module: 'settings', roles: ['owner', 'super_admin'] },
];

export function AdminSidebar({ collapsed, onToggle, onNavClick, logoUrl, marketingCounts, websiteCounts }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { garageSlug } = useParams();
  const { user, logout, hasRole, hasPermission } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();

  // Build base path for admin routes
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => {
    // Check role-based access first
    if (item.roles && !hasRole(item.roles)) return false;
    // Check module permission
    if (item.module && !hasPermission(item.module)) return false;
    return true;
  });

  // Get garage name from user context
  const garageName = user?.tenant_slug ? user.tenant_slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : t('app_name');

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-slate-900 text-white transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && (
            <Link to={basePath} className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
              ) : (
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5" />
                </div>
              )}
              <span className="font-bold text-lg font-['Barlow_Condensed'] truncate max-w-[150px]">
                {garageName}
              </span>
            </Link>
          )}
          {collapsed && (
            <Link to={basePath} className="mx-auto">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
              ) : (
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5" />
                </div>
              )}
            </Link>
          )}
          <button
            onClick={onToggle}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {filteredNavItems.map((item) => {
              const fullPath = `${basePath}${item.path}`;
              const isActive = location.pathname === fullPath || 
                (item.path !== '' && location.pathname.startsWith(fullPath));
              return (
                <Link
                  key={item.path}
                  to={fullPath}
                  onClick={() => { if (isActive) onNavClick?.(); }}
                  data-testid={`nav-${item.label}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  title={collapsed ? t(item.label) : undefined}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="font-medium">{t(item.label)}</span>}
                  {item.module === 'marketing' && marketingCounts && (() => {
                    const total = (marketingCounts.new_referrals || 0) + (marketingCounts.pending_parts_requests || 0) + (marketingCounts.scheduled_campaigns || 0);
                    return total > 0 ? <span className={`${collapsed ? '' : 'ml-auto'} inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-bold rounded-full bg-blue-500 text-white`}>{total}</span> : null;
                  })()}
                  {item.path === '/website' && websiteCounts && (() => {
                    const total = (websiteCounts.pending_bookings || 0) + (websiteCounts.pending_reviews || 0);
                    return total > 0 ? <span className={`${collapsed ? '' : 'ml-auto'} inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-bold rounded-full bg-blue-500 text-white`}>{total}</span> : null;
                  })()}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Info & Actions */}
        <div className="border-t border-slate-800 p-4 space-y-2">
          {!collapsed && user && (
            <div className="px-3 py-2 bg-slate-800 rounded-lg mb-2">
              <p className="font-medium text-sm truncate">{user.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user.role.replace('_', ' ')}</p>
            </div>
          )}
          
          <button
            onClick={toggleLanguage}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
            data-testid="admin-language-toggle"
          >
            <Globe className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>{language === 'en' ? 'বাংলা' : 'English'}</span>}
          </button>

          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-red-400 hover:bg-red-900/20 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>{t('nav_logout')}</span>}
          </button>
        </div>
      </div>
    </aside>
  );
}

export function AdminHeader({ sidebarCollapsed }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin') return t('dashboard_title');
    if (path.includes('/jobs')) return t('jobs_title');
    if (path.includes('/customers')) return t('customers_title');
    if (path.includes('/inventory')) return t('inventory_title');
    if (path.includes('/invoices')) return t('invoices_title');
    if (path.includes('/reports')) return t('reports_title');
    if (path.includes('/settings')) return t('settings_title');
    return '';
  };

  return (
    <header 
      className={`fixed top-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 z-30 transition-all duration-300 ${
        sidebarCollapsed ? 'left-16' : 'left-64'
      }`}
    >
      <h1 className="text-xl font-semibold text-slate-900 font-['Barlow_Condensed']">
        {getPageTitle()}
      </h1>

      <div className="flex items-center gap-4">
        <Link to="/" className="text-sm text-slate-600 hover:text-blue-800">
          {t('nav_home')}
        </Link>
      </div>
    </header>
  );
}

export function MobileBottomNav({ onNavClick, marketingCounts, websiteCounts }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { garageSlug } = useParams();
  const { t, toggleLanguage, language } = useLanguage();
  const { user, logout, hasRole, hasPermission } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  const handleLogout = () => {
    setMoreOpen(false);
    logout();
    navigate('/login');
  };

  // Primary items shown directly in the bottom bar
  const primaryItems = [
    { path: `${basePath}`, label: 'nav_dashboard', icon: LayoutDashboard, module: 'dashboard' },
    { path: `${basePath}/jobs`, label: 'nav_jobs', icon: Wrench, module: 'jobs' },
    { path: `${basePath}/invoices`, label: 'nav_invoices', icon: FileText, module: 'invoices' },
  ].filter(item => !item.module || hasPermission(item.module));

  // Items inside the "More" sheet
  const moreItems = [
    { path: `${basePath}/inventory`, label: 'nav_inventory', icon: Package, module: 'inventory' },
    { path: `${basePath}/services`, label: 'nav_services', icon: Cog, module: 'services' },
    { path: `${basePath}/customers`, label: 'nav_customers', icon: Users, module: 'customers' },
    { path: `${basePath}/employees`, label: 'nav_employees', icon: HardHat, module: 'employees' },
    { path: `${basePath}/marketing`, label: 'nav_marketing', icon: Megaphone, module: 'marketing', roles: ['owner', 'manager', 'super_admin'] },
    { path: `${basePath}/website`, label: 'nav_website', icon: Globe, module: 'settings', roles: ['owner', 'manager', 'super_admin'] },
    { path: `${basePath}/reports`, label: 'nav_reports', icon: BarChart3, module: 'reports', roles: ['owner', 'manager', 'super_admin'] },
    { path: `${basePath}/settings`, label: 'nav_settings', icon: Settings, module: 'settings', roles: ['owner', 'super_admin'] },
  ].filter(item => {
    if (item.roles && !hasRole(item.roles)) return false;
    if (item.module && !hasPermission(item.module)) return false;
    return true;
  });

  const isMoreActive = moreItems.some(item =>
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-50">
      <div className="flex items-center justify-around h-16">
        {primaryItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== basePath && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => { if (isActive) onNavClick?.(); }}
              className={`flex flex-col items-center justify-center flex-1 h-full ${
                isActive ? 'text-blue-800' : 'text-slate-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{t(item.label)}</span>
            </Link>
          );
        })}

        {/* More button */}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button
              className={`flex flex-col items-center justify-center flex-1 h-full ${
                isMoreActive ? 'text-blue-800' : 'text-slate-500'
              }`}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-xs mt-1">{t('nav_more') || 'More'}</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl pb-8">
            {/* Navigation grid */}
            <div className="grid grid-cols-4 gap-4 pt-4">
              {moreItems.map((item) => {
                const isActive = location.pathname === item.path ||
                  location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      setMoreOpen(false);
                      if (isActive) onNavClick?.();
                      navigate(item.path);
                    }}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-800'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="relative">
                      <item.icon className="w-6 h-6" />
                      {item.module === 'marketing' && marketingCounts && (() => {
                        const total = (marketingCounts.new_referrals || 0) + (marketingCounts.pending_parts_requests || 0) + (marketingCounts.scheduled_campaigns || 0);
                        return total > 0 ? <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-0.5 text-[9px] font-bold rounded-full bg-blue-500 text-white">{total}</span> : null;
                      })()}
                      {item.path.endsWith('/website') && websiteCounts && (() => {
                        const total = (websiteCounts.pending_bookings || 0) + (websiteCounts.pending_reviews || 0);
                        return total > 0 ? <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-0.5 text-[9px] font-bold rounded-full bg-blue-500 text-white">{total}</span> : null;
                      })()}
                    </div>
                    <span className="text-xs font-medium">{t(item.label)}</span>
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200 my-4" />

            {/* User info */}
            {user && (
              <div className="flex items-center gap-3 px-2 py-2 bg-slate-50 rounded-lg mb-3">
                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 truncate">{user.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user.role?.replace('_', ' ')}</p>
                </div>
              </div>
            )}

            {/* Language toggle & Logout */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  toggleLanguage();
                  setMoreOpen(false);
                }}
                className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-sm"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'বাংলা' : 'English'}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                {t('nav_logout')}
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}

export function AdminLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [navKey, setNavKey] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const [marketingCounts, setMarketingCounts] = useState(null);
  const [websiteCounts, setWebsiteCounts] = useState(null);
  const { token } = useAuth();

  // Expose sidebar width as a CSS custom property so floating action bars
  // (fixed-position) can offset themselves correctly without prop-drilling.
  useEffect(() => {
    const w = sidebarCollapsed ? '4rem' : '16rem';
    document.documentElement.style.setProperty('--admin-sidebar-w', w);
    return () => {
      document.documentElement.style.removeProperty('--admin-sidebar-w');
    };
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (token) {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      axios.get(`${API}/settings/garage`)
        .then(res => setLogoUrl(res.data?.logo_url || null))
        .catch(() => {});
      axios.get(`${API}/marketing/tab-counts`)
        .then(res => setMarketingCounts(res.data))
        .catch(() => {});
      axios.get(`${API}/website/counts`)
        .then(res => setWebsiteCounts(res.data))
        .catch(() => {});
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <AdminSidebar 
          collapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onNavClick={() => setNavKey(k => k + 1)}
          logoUrl={logoUrl}
          marketingCounts={marketingCounts}
          websiteCounts={websiteCounts}
        />
      </div>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav onNavClick={() => setNavKey(k => k + 1)} marketingCounts={marketingCounts} websiteCounts={websiteCounts} />

      {/* Main Content */}
      <main 
        key={navKey}
        className={`pb-20 md:pb-6 transition-all duration-300 ${
          sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
      >
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
